import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ExternalLink,
  EyeOff,
  Loader2,
  MessageSquare,
  Newspaper,
  Search,
  Send,
  Trash2,
} from 'lucide-react';
import { useState } from 'react';
import { EmptyState } from '../components/EmptyState';
import { Avatar } from '../components/Avatar';
import { HelpHint } from '../components/HelpHint';
import { Tooltip } from '../components/Hover';
import { PageNav } from '../components/PageNav';
import { api } from '../lib/api';
import { useSession } from '../lib/session';

const SOURCE_LABEL: Record<string, string> = {
  hackernews: 'Hacker News',
  devto: 'DEV',
};

/** "3 hours ago" reads better than a timestamp on a list of headlines. */
const since = (iso: string): string => {
  const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  if (minutes < 60) return `${Math.max(1, minutes)} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 36) return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
  const days = Math.round(hours / 24);
  return `${days} ${days === 1 ? 'day' : 'days'} ago`;
};

/**
 * Tech news, and what people here said about it.
 *
 * ## Where it comes from, and what that buys
 *
 * Hacker News and DEV, fetched hourly by the worker and read from our own
 * database. Neither needs an API key, which is the main reason they were chosen
 * over the ones that do — a news feed is not worth another credential to store
 * and rotate.
 *
 * The console never contacts either service. It cannot: it is served with
 * `connect-src 'self'`. That constraint turns out to be a feature — a reader's
 * browser never tells Hacker News who is reading, and the page keeps working
 * when an upstream is down.
 *
 * There are no thumbnails for the same reason: `img-src 'self'` means a remote
 * image would be blocked, and fetching one would leak the reader to whoever
 * hosts it. Headlines and a sentence are enough.
 *
 * ## Comments
 *
 * Everybody with a login can comment, customers included. It is a link to a news
 * article; there is nothing to protect. Somebody can always remove their own
 * remark without asking, and an admin can remove one that should not have been
 * posted — that rule is decided on the server and this page renders what it is
 * told, so the two cannot drift.
 */
export function NewsPage() {
  const queryClient = useQueryClient();
  const { user, can } = useSession();
  const [source, setSource] = useState('');
  const [query, setQuery] = useState('');
  /** Which story's comments are open. One at a time — this is a list. */
  const [open, setOpen] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  const news = useQuery({
    queryKey: ['news', source, query],
    queryFn: () => api.news({ source: source || undefined, q: query || undefined }),
  });

  const story = useQuery({
    queryKey: ['news-story', open],
    queryFn: () => api.newsStory(open as string),
    enabled: open !== null,
  });

  const refresh = async () => {
    setError(null);
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['news'] }),
      queryClient.invalidateQueries({ queryKey: ['news-story'] }),
    ]);
  };

  const comment = useMutation({
    mutationFn: () => api.addNewsComment(open as string, draft),
    onSuccess: async () => {
      setDraft('');
      await refresh();
    },
    onError: (e: Error) => setError(e.message),
  });

  const removeComment = useMutation({
    mutationFn: (id: string) => api.removeNewsComment(id),
    onSuccess: refresh,
    onError: (e: Error) => setError(e.message),
  });

  const hide = useMutation({
    mutationFn: (id: string) => api.hideNewsStory(id),
    onSuccess: async () => {
      setOpen(null);
      await refresh();
    },
    onError: (e: Error) => setError(e.message),
  });

  const items = news.data?.items ?? [];

  return (
    <div className="space-y-6">
      <PageNav crumbs={[{ label: 'Tech news' }]} />

      <div>
        <h1 className="flex items-center gap-1.5 font-heading text-2xl">
          Tech news
          <HelpHint>
            Headlines from Hacker News and DEV, collected once an hour. Your browser never contacts
            either site — we fetch it, so nobody outside learns what you are reading. Anybody here
            can comment, and you can always remove your own.
          </HelpHint>
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
          What is happening in networking and software, and what people here make of it.
        </p>
      </div>

      {/* ── Filters ──────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-end gap-3">
        <label className="min-w-56 flex-1 text-sm">
          Search the headlines
          <span className="relative mt-1 block">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2
                text-muted-foreground"
              aria-hidden="true"
            />
            <input
              className="input pl-9"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="fibre, postgres, kenya"
            />
          </span>
        </label>

        <label className="text-sm">
          Where from
          <select
            className="input mt-1"
            value={source}
            onChange={(event) => setSource(event.target.value)}
          >
            <option value="">Everywhere</option>
            {(news.data?.sources ?? []).map((name) => (
              <option key={name} value={name}>
                {SOURCE_LABEL[name] ?? name}
              </option>
            ))}
          </select>
        </label>
      </div>

      {error && (
        <p className="rounded-xl border border-danger-200 bg-danger-50 px-4 py-3 text-sm text-danger-700">
          {error}
        </p>
      )}

      {news.isPending && (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          Loading the headlines
        </p>
      )}

      {news.data && items.length === 0 && (
        <EmptyState
          icon={Newspaper}
          headline={query || source ? 'Nothing matches that' : 'No headlines yet'}
          body={
            query || source
              ? 'Try a shorter search, or look everywhere rather than one source.'
              : 'The first batch arrives within an hour of the system starting. Nothing is wrong — there is simply nothing here yet.'
          }
          {...(query || source
            ? {
                secondary: {
                  label: 'Clear the filters',
                  onClick: () => {
                    setQuery('');
                    setSource('');
                  },
                },
              }
            : {})}
        />
      )}

      <div className="space-y-3">
        {items.map((item) => (
          <article key={item.id} className="card">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                {/*
                  `rel="noreferrer"` as well as `noopener`: the second stops the
                  new tab reaching back into this one, the first stops the
                  destination being told which page sent the reader. Both matter
                  on a link the reader did not choose the destination of.
                */}
                <h2 className="font-heading text-lg leading-snug">
                  <a
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-navy-700 hover:underline"
                  >
                    {item.title}
                    <ExternalLink
                      className="ml-1.5 inline h-3.5 w-3.5 align-baseline text-muted-foreground"
                      aria-hidden="true"
                    />
                  </a>
                </h2>

                {item.summary && (
                  <p className="mt-1 text-sm text-muted-foreground">{item.summary}</p>
                )}

                <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                  <span className="badge bg-muted text-muted-foreground">
                    {SOURCE_LABEL[item.source] ?? item.source}
                  </span>
                  {item.author && <span>by {item.author}</span>}
                  <span>{since(item.publishedAt)}</span>
                  {item.score !== null && <span>{item.score} points</span>}
                  {item.tags.map((tag) => (
                    <span key={tag} className="badge bg-navy-50 text-navy-700">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              <div className="flex shrink-0 items-center gap-1">
                <button
                  type="button"
                  className="btn-quiet text-xs"
                  onClick={() => {
                    setOpen(open === item.id ? null : item.id);
                    setDraft('');
                    setError(null);
                  }}
                  aria-expanded={open === item.id}
                >
                  <MessageSquare className="h-3.5 w-3.5" aria-hidden="true" />
                  {item.comments === 0
                    ? 'Comment'
                    : `${item.comments} ${item.comments === 1 ? 'comment' : 'comments'}`}
                </button>

                {can('news:moderate') && (
                  <Tooltip label="Takes it off the list for everybody here" side="left">
                    <button
                      type="button"
                      className="rounded-lg p-2 text-muted-foreground hover:bg-muted"
                      disabled={hide.isPending}
                      onClick={() => hide.mutate(item.id)}
                    >
                      {hide.isPending && hide.variables === item.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                      ) : (
                        <EyeOff className="h-4 w-4" aria-hidden="true" />
                      )}
                      <span className="sr-only">Hide this story</span>
                    </button>
                  </Tooltip>
                )}
              </div>
            </div>

            {/* ── The thread ───────────────────────────────────────────── */}
            {open === item.id && (
              <div className="mt-4 space-y-3 border-t border-border pt-4">
                {story.isPending && (
                  <p className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                    Loading what people said
                  </p>
                )}

                {story.data?.comments.length === 0 && (
                  <p className="text-sm text-muted-foreground">
                    Nobody has said anything about this yet.
                  </p>
                )}

                <ul className="space-y-3">
                  {(story.data?.comments ?? []).map((entry) => (
                    <li key={entry.id} className="flex items-start justify-between gap-3">
                      <Avatar userId={entry.authorId} name={entry.author.name} size="sm" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs text-muted-foreground">
                          <span className="font-medium text-foreground">{entry.author.name}</span>
                          <span className="capitalize"> · {entry.author.role}</span>
                          <span> · {since(entry.createdAt)}</span>
                          {entry.editedAt && <span> · edited</span>}
                        </p>
                        {/*
                          Plain text in a paragraph. React escapes it, and
                          `whitespace-pre-wrap` keeps the line breaks somebody
                          typed without any markup being interpreted.
                        */}
                        <p className="mt-0.5 whitespace-pre-wrap text-sm">{entry.body}</p>
                      </div>

                      {entry.canRemove && (
                        <button
                          type="button"
                          className="btn-quiet shrink-0 text-xs"
                          disabled={removeComment.isPending}
                          onClick={() => removeComment.mutate(entry.id)}
                        >
                          {removeComment.isPending && removeComment.variables === entry.id && (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                          )}
                          <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                          <span className="sr-only">
                            Remove {entry.authorId === user?.id ? 'your comment' : 'this comment'}
                          </span>
                        </button>
                      )}
                    </li>
                  ))}
                </ul>

                {can('news:comment') && (
                  <form
                    className="space-y-2"
                    onSubmit={(event) => {
                      event.preventDefault();
                      comment.mutate();
                    }}
                  >
                    <label className="block text-sm">
                      <span className="sr-only">Your comment</span>
                      <textarea
                        className="input min-h-20"
                        value={draft}
                        onChange={(event) => setDraft(event.target.value)}
                        placeholder="What do you make of it?"
                        maxLength={2000}
                      />
                    </label>
                    <div className="flex items-center gap-3">
                      <button
                        type="submit"
                        className="btn-secondary px-3 py-1.5 text-sm"
                        disabled={comment.isPending || draft.trim().length === 0}
                      >
                        {comment.isPending ? (
                          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                        ) : (
                          <Send className="h-4 w-4" aria-hidden="true" />
                        )}
                        Post it
                      </button>
                      <span className="text-xs text-muted-foreground">
                        Everybody here can see this, and your name is on it.
                      </span>
                    </div>
                  </form>
                )}
              </div>
            )}
          </article>
        ))}
      </div>
    </div>
  );
}
